import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const stats = [
  { prefix: "₹", value: 100, suffix: " Cr+", label: "Target financing volume" },
  { prefix: "", value: 1000, suffix: "+", label: "Businesses onboarding goal" },
  { prefix: "", value: 24, suffix: "–48 hrs", label: "Target processing time" },
  { prefix: "", value: 100, suffix: "%", label: "Digital experience" },
];

function Counter({ to, duration = 1400 }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return <span ref={ref}>{n}</span>;
}

export default function Statistics() {
  return (
    <section id="stats" data-testid="stats-section" className="py-24 md:py-32 relative overflow-hidden bg-gradient-to-b from-slate-900 to-slate-950 text-white">
      <div aria-hidden className="absolute inset-0 opacity-30">
        <div className="absolute -top-32 left-1/3 w-[500px] h-[500px] rounded-full bg-emerald-700/50 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-emerald-500/40 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-emerald-300 mb-3">Our Ambition</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
            Numbers we&apos;re building toward.
          </h2>
          <p className="mt-4 text-slate-300 text-lg leading-relaxed">
            These are our platform goals during early access. Join us and help shape what Invoquity becomes at scale.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`stat-card-${i}`}
              className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl p-7"
            >
              <p className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                <span className="text-emerald-300">{s.prefix}</span>
                <Counter to={s.value} />
                <span className="text-emerald-300">{s.suffix}</span>
              </p>
              <p className="mt-2 text-sm text-slate-300">{s.label}</p>
            </motion.div>
          ))}
        </div>
        <p className="mt-8 text-xs text-slate-400 max-w-2xl">
          Note: These are future platform goals for Invoquity while we are in early access. Actual performance will be published post-launch.
        </p>
      </div>
    </section>
  );
}
